import { AuthStorage } from "utils/auth-storage";
import React, { useState, useCallback, useMemo, useRef } from "react";
import { Modal, Button, Tabs } from "antd";
import toast from "react-hot-toast";
import {
  ClockCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  HistoryOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  RollbackOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { ProjectServices } from "apis/Services/ProjectService";
import styles from "./ItemsPendingApprovals.module.css";

// ─── helpers ──────────────────────────────────────────────────────────────────

function handleNotAvailable(value) {
  return value || "N/A";
}

function buildRequestSentence(item) {
  const { firstName, lastName, values = {}, approvalType } = item;
  const { oldValue, newValue } = values;
  const fieldLabel =
    approvalType === "leadTime" ? "Lead Time" : "Sub Committed Delivery Date";
  const unit = approvalType === "leadTime" ? " week(s)" : "";

  let action;
  if (oldValue && newValue === "") {
    action = `is requesting to delete`;
  } else if (oldValue && newValue) {
    action = `is requesting to change`;
  } else {
    action = `is requesting to set`;
  }

  let detail = "";
  if (newValue === "" && oldValue) {
    detail = ` It was previously set to ${handleNotAvailable(oldValue)}${unit}.`;
  } else if (oldValue && newValue) {
    detail = ` from ${handleNotAvailable(oldValue)}${unit} to ${handleNotAvailable(newValue)}${unit}`;
  } else if (newValue) {
    detail = ` to ${handleNotAvailable(newValue)}${unit}`;
  }

  return `${firstName} ${lastName} ${action} the value of ${fieldLabel}${detail}`;
}

function buildHistorySentence(item) {
  const {
    managerFirstName,
    managerLastName,
    subContractorFirstName,
    subContractorLastName,
    decision,
    approvalType,
    oldValue,
    newValue,
  } = item;
  const fieldLabel =
    approvalType === "leadTime" ? "Lead Time" : "Sub Committed Delivery Date";
  const unit = approvalType === "leadTime" ? " week(s)" : "";
  const verb = decision === "Approve" ? "approved" : "rejected";

  let requestType;
  if (oldValue && newValue === "") {
    requestType = "request to delete the";
  } else if (oldValue && newValue) {
    requestType = "request to change the";
  } else {
    requestType = "request to set the";
  }

  let detail = "";
  if (newValue === "" && oldValue) {
    detail = ` It was previously set to ${oldValue}${unit}.`;
  } else if (oldValue && newValue) {
    detail = ` from ${oldValue}${unit} to ${newValue}${unit}`;
  } else if (newValue) {
    detail = ` to ${newValue}${unit}`;
  }

  return `${managerFirstName} ${managerLastName} has ${verb} ${subContractorFirstName} ${subContractorLastName}'s ${requestType} ${fieldLabel}${detail}`;
}

// ─── sub-components ───────────────────────────────────────────────────────────

function SubmittalHeader({ item }) {
  const parts = [
    item.submittalNumber,
    item.submittalRevision ? `.${item.submittalRevision}` : null,
    item.specTitle ? ` — ${item.specTitle}` : null,
  ]
    .filter(Boolean)
    .join("");

  return (
    <div className={styles.submittalHeader}>
      <span className={styles.submittalHeaderDot} />
      <span className={styles.submittalHeaderText}>
        {parts || item.specTitle}
      </span>
    </div>
  );
}

function ApprovalCard({
  item,
  itemKey,
  voteMap,
  onVote,
  onRevert,
  onNoteChange,
  disabled,
}) {
  const key = itemKey(item);
  const vote = voteMap[key];
  const hasVoted = !!vote;
  const isApproved = vote === "Approve";

  return (
    <div
      className={[
        styles.approvalCard,
        hasVoted
          ? isApproved
            ? styles.cardApproved
            : styles.cardRejected
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={styles.cardBody}>
        <p className={styles.cardSentence}>{buildRequestSentence(item)}</p>
        <span className={styles.cardTimestamp}>{item.timestamp}</span>
      </div>

      <div className={styles.cardActions}>
        {!hasVoted ? (
          <>
            <button
              className={`${styles.voteBtn} ${styles.approveBtn}`}
              onClick={() => onVote(item, "Approve")}
              disabled={disabled}
            >
              <CheckOutlined />
              Approve
            </button>
            <button
              className={`${styles.voteBtn} ${styles.rejectBtn}`}
              onClick={() => onVote(item, "Reject")}
              disabled={disabled}
            >
              <CloseOutlined />
              Reject
            </button>
          </>
        ) : (
          <>
            <div className={styles.votedBadge}>
              {isApproved ? (
                <CheckCircleFilled className={styles.approvedIcon} />
              ) : (
                <CloseCircleFilled className={styles.rejectedIcon} />
              )}
              <span>{isApproved ? "Approved" : "Rejected"}</span>
            </div>
            <button
              className={`${styles.voteBtn} ${styles.revertBtn}`}
              onClick={() => onRevert(item)}
              disabled={disabled}
            >
              <RollbackOutlined />
              Revert
            </button>
          </>
        )}
      </div>

      {hasVoted && (
        <div className={styles.noteRow}>
          <input
            className={styles.noteInput}
            placeholder="Add a note (optional)…"
            maxLength={140}
            disabled={disabled}
            onBlur={(e) => onNoteChange(item, e.target.value)}
          />
        </div>
      )}
    </div>
  );
}

function HistoryCard({ item }) {
  const isApproved = item.decision === "Approve";

  return (
    <div
      className={`${styles.historyCard} ${
        isApproved ? styles.historyApproved : styles.historyRejected
      }`}
    >
      <div className={styles.historyDecisionBadge}>
        {isApproved ? (
          <CheckCircleFilled className={styles.approvedIcon} />
        ) : (
          <CloseCircleFilled className={styles.rejectedIcon} />
        )}
      </div>
      <div className={styles.cardBody}>
        <p className={styles.cardSentence}>{buildHistorySentence(item)}</p>
        {item.notes && (
          <p className={styles.historyNotes}>
            <span className={styles.notesLabel}>Notes:</span> {item.notes}
          </p>
        )}
        <span className={styles.cardTimestamp}>{item.timestamp}</span>
      </div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className={styles.emptyState}>
      <ClockCircleOutlined className={styles.emptyIcon} />
      <p>{message}</p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className={styles.loadingState}>
      {[...Array(3)].map((_, i) => (
        <div key={i} className={styles.skeleton}>
          <div className={styles.skeletonLine} style={{ width: "80%" }} />
          <div className={styles.skeletonLine} style={{ width: "55%" }} />
        </div>
      ))}
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

/**
 * ItemsPendingApprovals
 *
 * Props:
 *   company_id         – string
 *   project_id         – string  (the "id" param)
 *   accessLevel        – number  (hide button when < 2)
 *   isLoading          – bool    (disable while parent loads)
 *   itemsPendingApprovals – array of live entry IDs for the badge count
 *   deletedEntryIds     – array of entries deleted since the last data load
 */
export default function ItemsPendingApprovalsModal({
  company_id,
  project_id,
  accessLevel = 0,
  isLoading = false,
  itemsPendingApprovals = [],
  deletedEntryIds = [],
  projectName,
  onSuccess,
  beginSyncActivity,
  finishSyncActivity,
  failSyncActivity,
}) {
  const [open, setOpen] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [pendingItems, setPendingItems] = useState([]);
  const [historyItems, setHistoryItems] = useState([]);
  // A request started before deletion may still return the deleted item's approvals.
  const activePendingItems = useMemo(() => {
    const deletedEntryIdSet = new Set(deletedEntryIds.map(String));
    return pendingItems.filter(
      (item) => !deletedEntryIdSet.has(String(item.entryID)),
    );
  }, [deletedEntryIds, pendingItems]);

  // vote + notes state: keyed by a stable item identifier
  const [voteMap, setVoteMap] = useState({}); // id -> "Approve" | "Reject"
  const [noteMap, setNoteMap] = useState({}); // id -> string

  const hasPending = itemsPendingApprovals?.length > 0;

  // ── stable item key ────────────────────────────────────────────────────────
  const itemKey = useCallback(
    (item) => item.approvalID ?? `${item.entryID}:${item.approvalType}`,
    [],
  );

  // ── open / fetch ───────────────────────────────────────────────────────────
  const handleOpen = useCallback(() => {
    if (accessLevel < 2) return;

    setOpen(true);
    setFetching(true);
    setVoteMap({});
    setNoteMap({});

    const code = AuthStorage.getItem("token");
    const email = AuthStorage.getItem("email");
    const payload = { company_id, project_id, code, email };

    Promise.allSettled([
      ProjectServices.getItemsPendingApprovals(payload),
      ProjectServices.getItemsPendingApprovalsHistory(payload),
    ]).then(([pendingResult, historyResult]) => {
      if (pendingResult.status === "fulfilled") {
        setPendingItems(pendingResult.value || []);
      } else {
        console.error("Failed to load pending approvals", pendingResult.reason);
        setPendingItems([]);
      }
      if (historyResult.status === "fulfilled") {
        setHistoryItems(historyResult.value || []);
      } else {
        console.error("Failed to load approval history", historyResult.reason);
        setHistoryItems([]);
      }
      setFetching(false);
    });
  }, [accessLevel, company_id, project_id]);

  const handleClose = useCallback(
    (shouldReload = false) => {
      setOpen(false);
      setPendingItems([]);
      setHistoryItems([]);
      setVoteMap({});
      setNoteMap({});
      if (shouldReload) {
        onSuccess?.();
      }
    },
    [onSuccess],
  );

  // ── cancel with unsaved-changes guard ─────────────────────────────────────
  const handleCancel = useCallback(() => {
    if (submittingRef.current) return;
    if (activePendingItems.some((item) => voteMap[itemKey(item)])) {
      Modal.confirm({
        title: "Unsaved changes",
        content:
          "You have decisions that haven't been submitted yet. If you close now, your changes will not be saved.",
        okText: "Leave anyway",
        cancelText: "Stay",
        okButtonProps: { danger: true, style: { borderRadius: "9999px" } },
        cancelButtonProps: { style: { borderRadius: "9999px" } },
        centered: true,
        className: styles.confirmModal,
        onOk: () => handleClose(false),
      });
    } else {
      handleClose(false);
    }
  }, [activePendingItems, voteMap, itemKey, handleClose]);

  // ── vote interactions ──────────────────────────────────────────────────────
  const handleVote = useCallback(
    (item, decision) => {
      setVoteMap((prev) => ({ ...prev, [itemKey(item)]: decision }));
    },
    [itemKey],
  );

  const handleRevert = useCallback(
    (item) => {
      setVoteMap((prev) => {
        const next = { ...prev };
        delete next[itemKey(item)];
        return next;
      });
      setNoteMap((prev) => {
        const next = { ...prev };
        delete next[itemKey(item)];
        return next;
      });
    },
    [itemKey],
  );

  const handleNoteChange = useCallback(
    (item, value) => {
      setNoteMap((prev) => ({ ...prev, [itemKey(item)]: value }));
    },
    [itemKey],
  );

  // ── submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    const voted = activePendingItems.filter((item) => voteMap[itemKey(item)]);
    if (!voted.length || submittingRef.current) {
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);

    const code = AuthStorage.getItem("token");
    const email = AuthStorage.getItem("email");

    // Build the same `values` array shape the old holdingItems produced
    const values = voted.map((item) => ({
      ...item,
      projectName: projectName,
      decision: voteMap[itemKey(item)],
      notes: noteMap[itemKey(item)] || "",
    }));

    const data = {
      company_id,
      project_id,
      code,
      email,
      values,
    };
    const syncToken = beginSyncActivity?.("Submitting approval decisions");

    try {
      await ProjectServices.sendItemsPendingApprovals(data);
      finishSyncActivity?.(syncToken);
      toast.success("Your approval decisions are now applied");
      handleClose(true);
    } catch (err) {
      console.error("Failed to submit approvals", err);
      failSyncActivity?.(
        syncToken,
        "Failed to submit approval decisions. You may no longer be synced to the latest version.",
      );
      toast.error("Failed to submit approval decisions. Please try again.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [
    beginSyncActivity,
    failSyncActivity,
    finishSyncActivity,
    activePendingItems,
    voteMap,
    noteMap,
    itemKey,
    project_id,
    company_id,
    handleClose,
    projectName,
  ]);

  const handleAcceptAll = useCallback(() => {
    const decisions = {};
    activePendingItems.forEach((item) => {
      decisions[itemKey(item)] = "Approve";
    });
    setVoteMap(decisions);
  }, [activePendingItems, itemKey]);

  // ── group items by submittal ───────────────────────────────────────────────
  function groupBySubmittal(items) {
    const groups = [];
    const seen = new Set();
    items.forEach((item) => {
      const entryID = String(item.entryID);
      if (!seen.has(entryID)) {
        seen.add(entryID);
        groups.push({ entryID, item, children: [] });
      }
      groups.find((g) => g.entryID === entryID).children.push(item);
    });
    return groups;
  }

  const pendingGroups = groupBySubmittal(activePendingItems);
  const historyGroups = groupBySubmittal(historyItems);
  const votedCount = activePendingItems.filter(
    (item) => voteMap[itemKey(item)],
  ).length;

  // ── render ─────────────────────────────────────────────────────────────────
  if (accessLevel < 2) return null;

  return (
    <>
      {/* ── trigger button ── */}
      <Button
        className={[
          styles.triggerButton,
          hasPending ? styles.triggerButtonActive : "",
          isLoading ? styles.triggerButtonDisabled : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={handleOpen}
        disabled={isLoading}
      >
        <span>Items Pending Approval</span>
        {hasPending && (
          <span className={styles.triggerBadge}>
            {itemsPendingApprovals.length}
          </span>
        )}
      </Button>

      {/* ── modal ── */}
      <Modal
        open={open}
        onCancel={handleCancel}
        footer={null}
        width={620}
        destroyOnHidden
        className={styles.modal}
        centered
        closable={!submitting}
        title={
          <div className={styles.modalHeader}>
            <ClockCircleOutlined className={styles.modalHeaderIcon} />
            <div>
              <div className={styles.modalHeaderTitle}>Pending Approvals</div>
              <div className={styles.modalHeaderSub}>
                Review and respond to change requests
              </div>
            </div>
          </div>
        }
      >
        <Tabs
          defaultActiveKey="pending"
          className={styles.tabs}
          items={[
            {
              key: "pending",
              label: (
                <span className={styles.tabLabel}>
                  Requests
                  {activePendingItems.length > 0 && !fetching && (
                    <span className={styles.tabBadge}>
                      {activePendingItems.length}
                    </span>
                  )}
                </span>
              ),
              children: fetching ? (
                <LoadingState />
              ) : pendingGroups.length === 0 ? (
                <EmptyState message="No items are currently pending approval." />
              ) : (
                <div className={styles.tabContent}>
                  <div className={styles.itemsScrollArea}>
                    {pendingGroups.map(({ item: headerItem, children }) => (
                      <div
                        key={headerItem.entryID}
                        className={styles.submittalGroup}
                      >
                        <SubmittalHeader item={headerItem} />
                        {children.map((child) => (
                          <ApprovalCard
                            key={itemKey(child)}
                            item={child}
                            itemKey={itemKey}
                            voteMap={voteMap}
                            onVote={handleVote}
                            onRevert={handleRevert}
                            onNoteChange={handleNoteChange}
                            disabled={submitting}
                          />
                        ))}
                      </div>
                    ))}
                  </div>

                  {/* footer actions */}
                  <div className={styles.footerActions}>
                    <button
                      type="button"
                      className={styles.acceptAllBtn}
                      onClick={handleAcceptAll}
                      disabled={submitting}
                      aria-busy={submitting}
                    >
                      <CheckOutlined />
                      Approve All
                    </button>
                    <button
                      type="button"
                      className={[
                        styles.submitBtn,
                        votedCount === 0 || submitting
                          ? styles.submitBtnDisabled
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      disabled={votedCount === 0 || submitting}
                      aria-busy={submitting}
                      onClick={handleSubmit}
                    >
                      <SendOutlined />
                      Submit {votedCount > 0 ? `(${votedCount})` : ""}
                    </button>
                  </div>
                </div>
              ),
            },
            {
              key: "history",
              label: (
                <span className={styles.tabLabel}>
                  <HistoryOutlined style={{ marginRight: 4 }} />
                  Change History
                </span>
              ),
              children: fetching ? (
                <LoadingState />
              ) : historyGroups.length === 0 ? (
                <EmptyState message="No change history yet." />
              ) : (
                <div className={styles.tabContent}>
                  <div className={styles.itemsScrollArea}>
                    {historyGroups.map(({ item: headerItem, children }) => (
                      <div
                        key={headerItem.entryID}
                        className={styles.submittalGroup}
                      >
                        <SubmittalHeader item={headerItem} />
                        {children.map((child, i) => (
                          <HistoryCard key={i} item={child} />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ),
            },
          ]}
        />
      </Modal>
    </>
  );
}
